import { supabase } from './supabase';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'dining' | 'social' | 'exploration' | 'milestone' | 'special';
  points: number;
  difficulty: 'easy' | 'medium' | 'hard' | 'epic' | 'legendary';
  progress?: number;
  maxProgress?: number;
  unlocked: boolean;
  criteria: string[];
}

export const calculateAchievements = async (userId: string): Promise<Achievement[]> => {
  try {
    // Get user's profile and activity data
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('dinners_count, created_at, favorite_cuisines, dietary_preferences, languages')
      .eq('id', userId)
      .single();

    if (profileError) throw profileError;

    // Get dinner participation data
    const { data: dinnerParticipations, error: dinnerError } = await supabase
      .from('dinner_participants')
      .select('dinner_id, status')
      .eq('user_id', userId);

    if (dinnerError) throw dinnerError;

    // Get blind date participation data
    const { data: blindDateParticipations, error: blindDateError } = await supabase
      .from('blind_date_participants')
      .select('blind_date_id, status')
      .eq('user_id', userId);

    if (blindDateError) throw blindDateError;

    // Get drinking night participation data
    const { data: drinkingNightParticipations, error: drinkingError } = await supabase
      .from('drinking_night_participants')
      .select('drinking_night_id, status')
      .eq('user_id', userId);

    if (drinkingError) throw drinkingError;

    // Get friends count
    const { count: friendsCount, error: friendsError } = await supabase
      .from('friends_accepted')
      .select('*', { count: 'exact', head: true })
      .or(`user1_id.eq.${userId},user2_id.eq.${userId}`);

    if (friendsError) throw friendsError;

    // Calculate days active
    const daysActive = Math.floor(
      (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
    );

    // Define achievements with points and criteria
    const achievements: Achievement[] = [
      // Leichte Achievements (1-10 points)
      {
        id: 'first-dinner',
        name: 'Erstes Dinner',
        description: 'Besuche den erstes Dinner',
        icon: 'utensils',
        category: 'milestone',
        points: 5,
        difficulty: 'easy',
        unlocked: (dinnerParticipations?.length || 0) >= 1,
        criteria: ['Besuche 1 Dinner Event']
      },
      {
        id: 'dietary-conscious',
        name: 'Überlegte Diät',
        description: 'Wähle Ernährungs Präferenzen für dein Profil',
        icon: 'leaf',
        category: 'milestone',
        points: 5,
        difficulty: 'easy',
        unlocked: (profile.dietary_preferences?.length || 0) > 0,
        criteria: ['Wähle min. eine Ernährungspräferenz für dein Profil']
      },
      {
        id: 'first-friend',
        name: 'Das Miteinander beginnt',
        description: 'Füge deinen ersten Freund hinzu',
        icon: 'users',
        category: 'social',
        points: 10,
        difficulty: 'easy',
        unlocked: (friendsCount || 0) >= 1,
        criteria: ['Habe min. einen Freund in der App']
      },

      // Medium Achievements (11-50 points)
      {
        id: 'social-butterfly',
        name: 'Social Butterfly',
        description: 'Verbinde dich mit 20+ gleichgesinnten Kulinarik-Fans',
        icon: 'users',
        category: 'social',
        points: 30,
        difficulty: 'medium',
        progress: friendsCount || 0,
        maxProgress: 20,
        unlocked: (friendsCount || 0) >= 20,
        criteria: ['Habe min. 20 Freunde in der App', 'Halte Verbindungen aufrecht']
      },
      {
        id: 'cuisine-explorer',
        name: 'Hauptsache was neues probieren!',
        description: 'Probiere Essen aus min. 5 verschiedenen Ländern',
        icon: 'compass',
        category: 'dining',
        points: 25,
        difficulty: 'medium',
        progress: profile.favorite_cuisines?.length || 0,
        maxProgress: 5,
        unlocked: (profile.favorite_cuisines?.length || 0) >= 5,
        criteria: ['Probiere min. 5 verschiedene Landesküchen', 'Bewerte jedes Erlebnis']
      },
      {
        id: 'regular-diner',
        name: 'Stammgast',
        description: 'Besuche 10 Dinner Events',
        icon: 'utensils',
        category: 'dining',
        points: 40,
        difficulty: 'medium',
        progress: dinnerParticipations?.length || 0,
        maxProgress: 10,
        unlocked: (dinnerParticipations?.length || 0) >= 10,
        criteria: ['Besuche min. 10 verschiedene Dinner Events', 'Bewerte alle 10 Dinner']
      },

      // Hard Achievements (51-100 points)
      {
        id: 'master-host',
        name: 'Der beste Gastgeber',
        description: 'Hoste 10 Dinner Events mit großem Erfolg',
        icon: 'crown',
        category: 'dining',
        points: 75,
        difficulty: 'hard',
        progress: profile.dinners_count || 0,
        maxProgress: 10,
        unlocked: (profile.dinners_count || 0) >= 10,
        criteria: [
          'Hoste 10 Dinner events',
          'Bleib über einem 4,5* Rating',
          'Habe min. 30 Besucher'
        ]
      },
      {
        id: 'language-master',
        name: 'Polyglot in der Mache',
        description: 'Verbinde dich mit Sprechern von min. 5 Sprachen',
        icon: 'globe',
        category: 'exploration',
        points: 80,
        difficulty: 'hard',
        progress: profile.languages?.length || 0,
        maxProgress: 5,
        unlocked: (profile.languages?.length || 0) >= 5,
        criteria: [
          'Verbinde dich mit Sprechern von 5 Sprachen',
          'Halte erfolgreich Dinner mit allen 5 ab',
          'Erhalte ein gutes Feedback von allen 5'
        ]
      },

      // Epic Achievements (101-500 points)
      {
        id: 'global-connector',
        name: 'Globales Netzwerk',
        description: 'Verbinde dich mit 100+ Kulinarik-Fans weltweit',
        icon: 'network',
        category: 'social',
        points: 300,
        difficulty: 'epic',
        progress: friendsCount || 0,
        maxProgress: 100,
        unlocked: (friendsCount || 0) >= 100,
        criteria: [
          'Verbinde dich mit min. 100 Usern',
          'Habe Freunde aus min. 10 Ländern',
          'Erhalte diese Verbindungen aufrecht',
          'Organisiere Dinner Events in 3 Ländern'
        ]
      },

      // Legendary Achievements (501-1000 points)
      {
        id: 'community-legend',
        name: 'Community Legende',
        description: 'Werde eine Legende in der Meet&Eat Community',
        icon: 'crown',
        category: 'special',
        points: 1000,
        difficulty: 'legendary',
        unlocked: daysActive >= 365 && 
                 (profile.dinners_count || 0) >= 50 && 
                 (friendsCount || 0) >= 100 &&
                 (profile.favorite_cuisines?.length || 0) >= 20,
        criteria: [
          'Sei aktiv für über 1 Jahr',
          'Hoste 50+ erfolgreiche Dinner',
          'Habe 100+ Freunde in der App',
          'Probiere 20+ Landesküchen',
          'Bleibe über einem 4,5 Rating',
          'Erstelle min. 3 Reviews',
          'Empfehle die App an min. 5 Freunde'
        ]
      },
      {
        id: 'master-of-ceremonies',
        name: 'Traumhost',
        description: 'Sei der ultimative Dinner Host',
        icon: 'landmark',
        category: 'special',
        points: 750,
        difficulty: 'legendary',
        progress: profile.dinners_count || 0,
        maxProgress: 100,
        unlocked: (profile.dinners_count || 0) >= 100,
        criteria: [
          'Hoste 100 erfolgreiche Dinner',
          'Halte ein 4.5 Rating aufrecht',
          'Erhalte min. 10 "Bester Host" Auszeichungen',
          'Create hosting guidelines',
          'Mentor 10 new hosts'
        ]
      }
    ];

    return achievements;
  } catch (err) {
    console.error('Error calculating achievements:', err);
    return [];
  }
};